<template>
  <div v-if="storeSchema">
    <v-row
      v-if="masterContext() || tabContext() || stepContext()"
      v-bind="storeSchema.attributes.attributesLayout"
    >
      <v-col sm="12">
        <component
          v-if="masterContext() && storeSchema.master.visible"
          :is="storeSchema.master.type"
          :schema="storeSchema.master"
          :model="storeModel"
          :mode="context"
          :page="this.page"
        />

        <i4proTabs
          v-if="tabContext()"
          :page="page"
          :attributes="storeSchema.tabs.attributes"
          :content="storeSchema.tabs.content"
          :mode="context"
          :tabsSchema="storeSchema.tabs"
          @active-tab="setActiveTab"
        />

        <component
          v-if="stepContext()"
          :is="storeSchema.steps.attributes.name"
          :schema="storeSchema.steps"
          :mode="context"
          :origin="origin"
          :page="page"
          :stepEditable="false"
          :stepIcon="'check'"
          :stepIconColor="'primary'"
          @active-step="setActiveStep"
        />
      </v-col>

      <v-col v-if="tabContext()" class="action-no-padding">
        <i4proActions
          v-if="renderActions"
          :page="page"
          :mode="context"
          :origin="origin"
          :tag="'v-card-actions'"
          :tab="activeTab"
          :isValid="valid"
          :child="child"
        />
      </v-col>
    </v-row>

    <v-form
      v-model="valid"
      v-if="storeSchema.groups.length || storeSchema.fields.length"
      ref="form"
    >
      <v-row
        v-bind="storeSchema.attributes.attributesLayout"
        :class="storeSchema.attributes.fields && 'ma-auto'"
      >
        <template v-if="storeSchema.groups.length">
          <i4proColumnGroup
            v-for="(group, index) in storeSchema.groups"
            :schema="group"
            :key="index"
            :mode="context"
            :page="page"
          />
        </template>

        <i4proColumnFields
          v-if="storeSchema.fields.length"
          :class="storeSchema.attributes.attributesLayout.reverseOrder && 'flex-item'"
          :page="page"
          :mode="context"
          :schema="storeSchema.fields"
          :background="storeSchema.attributes.fields"
        />

        <v-col
          sm="12"
          :class="[context !== 'sidebar' && 'action-no-padding', !actionContext() && 'pa-0', storeSchema.attributes.fields && 'px-0']"
        >
          <i4proActions
            v-if="renderActions"
            :page="page"
            :mode="context"
            :origin="origin"
            :tag="'v-card-actions'"
            :tab="activeTab"
            :isValid="valid"
            :child="child"
          />
        </v-col>
      </v-row>
    </v-form>

    <i4proDialog
      v-if="!!showDialog"
      @close="close"
      :page="component.page"
      :currentComponent="component.component"
      :componentParams="{ page: component.page, context: component.context, origin: 'dialog' }"
      v-bind="configDialog"
      :actionsToolbar="configDialog.actionsToolbar"
      :toolbar="configDialog.toolbar"
    />
  </div>
</template>

<script>
import * as HANDLERS from '@/helpers/types/handlers';
import store from '@/store';
import { sleep } from '@/helpers/page';

export default {
  components: {
    i4proActions: () => import('@/components/partials/i4pro-actions'),
    i4proTabs: () => import('@/components/tabs/i4pro-tabs'),
    i4proDetailPage: () => import('@/components/detail/i4pro-detail-page'),
    i4proDetailDrawer: () => import('@/components/detail/i4pro-detail-drawer'),
    i4proColumnFields: () => import('@/components/partials/i4pro-column-fields'),
    i4proDialog: () => import('@/components/dialogs/i4pro-dialog'),
    i4proColumnGroup: () => import('@/components/partials/i4pro-column-group'),
    i4proHorizontalStep: () => import('@/components/steps/i4pro-horizontal-step'),
    i4proVerticalStep: () => import('@/components/steps/i4pro-vertical-step')
  },

  props: {
    page: { type: String, required: true },
    context: { type: String, required: true, default: 'form' },
    origin: { type: String, default: 'page' },
    renderActions: { type: Boolean, default: true },
    child: { type: Boolean, default: false }
  },

  data: () => ({
    activeTab: 0,
    activeStep: 0,
    valid: false,
    showDialog: false,
    dialog: '',
    configDialog: {},
    component: {}
  }),

  computed: {
    storeSchema() {
      return store.getters['page/schema']({ page: this.page, mode: this.context });
    },

    attributesDialog() {
      return store.getters['layout/getDialogs'](this.dialog);
    },

    storeModel() {
      return store.getters['page/model']({ page: this.page, mode: 'master' });
    }
  },

  watch: {
    attributesDialog(newValue, oldValue) {
      if (!newValue.toolbar.title) {
        newValue.toolbar.title = this.$store.getters['page/title'](this.component.page);
      }

      this.configDialog = newValue;
    },
    showDialog(newValue) {
      if (newValue) {
        if (this.configDialog.titlePage) {
          this.configDialog.toolbar.title = this.$store.getters['page/title'](this.component.page);
        }

        this.configDialog.titlePage = true;
      }
    }
  },

  events: {
    [HANDLERS.VALIDATE]: 'validate',
    [HANDLERS.RESPONSE_TAB_VALIDATE]: 'tabIsValid',
    [HANDLERS.RENDER_COMPONENT_IN_FORM]: 'render',
    [HANDLERS.SHOW_GROUPS]: 'showGroups',
    [HANDLERS.HIDE_GROUP]: 'hideGroup',
    [HANDLERS.UPDATE_COMPONENT]: 'updateComponent'
  },

  methods: {
    setActiveTab(tab) {
      this.activeTab = tab;
      this.$emit('change-tab', tab);
      this.valid = false;
    },

    showGroups({ page, mode, tab, step }) {
      if (tab || step || this.page !== page || this.context !== mode) return;

      this.$store.dispatch('page/showGroups', { page, mode, tab, step });
    },

    hideGroup({ page, mode, tab, step, group }) {
      if (tab || step || this.page !== page || this.context !== mode) return;

      this.$store.dispatch('page/hideGroup', { page, mode, tab, step, group });
    },

    setActiveStep(step) {
      this.activeStep = step;
      this.$emit('change-step', step);
      this.valid = false;
    },

    tabContext() {
      if (this.storeSchema) return 'tabs' in this.storeSchema;
    },

    stepContext() {
      if (this.storeSchema) return 'steps' in this.storeSchema;
    },

    masterContext() {
      if (this.storeSchema) return 'master' in this.storeSchema;
    },

    actionContext() {
      return Object.keys(this.storeSchema.actions).length;
    },

    validate({ page, mode }) {
      if (page !== this.page || mode !== this.context) return;

      if (this.stepContext()) {
        this.$emit(HANDLERS.VALIDATE_STEP, page);
        return;
      }

      if (this.tabContext()) {
        this.$emit(HANDLERS.VALIDATE_TAB, page);
        return;
      }

      !this.$refs.form.validate(true) && this.showMessageRequired(false);

      this.$emit('validate-dialog', this.valid);
    },

    tabIsValid(e) {
      this.valid = e;

      this.showMessageRequired(e);
    },

    close(id) {
      this.showDialog = false;
      this.component = {};
    },

    updateComponent(params) {
      if (this.component.page === params.page) {
        this.showDialog = false;
        this.component.page = params.page;
        this.component.context = params.mode;
        this.component.component = params.mode === 'list' ? 'i4pro-list' : 'i4pro-form';
        this.showDialog = true;
      }
    },

    async render(params) {
      if (params.origin !== this.page) return;

      if (this.showDialog) {
        this.showDialog = false;
        this.component = {};
      }

      this.dialog = params.dialog;
      this.component.page = params.page;
      this.component.context = params.context;
      this.component.component = params.context === 'list' ? 'i4pro-list' : 'i4pro-form';

      await sleep(30);
      this.showDialog = true;
    },

    showMessageRequired(valid) {
      if (!valid) {
        store.dispatch('notification/showNotification', {
          color: 'error',
          text: 'Existem campos obrigatórios não preenchidos',
          showIconNotification: false
        });
      }
    }
  }
};
</script>

<style>
.flex-item {
  order: -1;
}
</style>
